import React, { useEffect, useState } from 'react';
import { Switch, TableCell, TableRow } from '@mui/material';
import lodash, { isNil, isNull } from 'lodash';
import BoxGrid from '../boxGrid/boxGrid';
import KogCheckbox from '../checkbox/kogCheckbox';
import KogInputAmountControlled from '@/main/components/shared/inputText/inputAmountControlled';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { SupplierFilterRequest, SupplierInfoList } from '@/main/models/request';
import { Control, UseFormSetValue } from 'react-hook-form';
import * as S from './styles';
import { SupplierRegistrationDataResponse } from '@/main/models/response';
import { currencyToNumber } from '@/main/services/utilsNumber';
import { getTranslateByKey } from '@/main/services/utils';
import { ContainerSwitch } from '../styles';

interface IOptionElement {
  content: string | React.ReactNode;
  width?: string | number;
}

export type IOptionGridCheckbox = {
  guid: string;
  elements: IOptionElement[];
};

interface IKOGBoxGridCheckboxProps {
  rowHeaders: string[] | React.ReactNode[];
  options: SupplierRegistrationDataResponse[];
  optionsSelected: SupplierInfoList[];
  setOptionsSelected: (data: SupplierInfoList[]) => void;
  onChangeCustom?: (data: SupplierInfoList[]) => void;
  formFilters: SupplierFilterRequest | null;
  setFormFilters: (formFilters: SupplierFilterRequest | null) => void;
  getGridData: (formFilters: SupplierFilterRequest) => void;
  hasShowMore: boolean;
  pageIndex: number;
  setPageIndex: (index: number) => void;
  isLoadingInfiniteScroll: boolean;
  idScrollTargetGrid: string;
  showRefundLimitColumn?: boolean;
  control?: Control<any>;
}

function KOGBoxGridCheckboxInverted({
  options,
  rowHeaders,
  optionsSelected,
  setOptionsSelected,
  onChangeCustom = () => {},
  formFilters,
  setFormFilters,
  getGridData,
  hasShowMore,
  pageIndex,
  setPageIndex,
  isLoadingInfiniteScroll,
  idScrollTargetGrid,
  showRefundLimitColumn = false,
  control
}: IKOGBoxGridCheckboxProps) {
  const [allSelected, setAllSelected] = useState(
    optionsSelected.length === options.length && options.length > 0
  );

  useEffect(() => {
    if (allSelected === true) {
      const allGuids = options.map(option => ({ guidSupplier: option.guid }));
      setOptionsSelected(allGuids);
      onChangeCustom(allGuids);
    }
  }, [options]);

  function handleToggleCheckbox(guid: string, isTrusted?: boolean) {
    const cloneOptionsSelected = lodash.cloneDeep(optionsSelected);
    const index = cloneOptionsSelected.findIndex(
      item => item.guidSupplier === guid
    );

    if (isTrusted !== undefined) {
      cloneOptionsSelected[index].isTrusted = isTrusted;
    } else {
      if (index > -1) {
        cloneOptionsSelected.splice(index, 1);
      } else {
        cloneOptionsSelected.push({
          guidSupplier: guid
        });
      }
    }

    const allSelecteds = cloneOptionsSelected.length === options.length;

    setAllSelected(allSelecteds);
    setOptionsSelected(cloneOptionsSelected);
    onChangeCustom(cloneOptionsSelected);
  }

  function handleToggleSelectAll() {
    if (allSelected) {
      setOptionsSelected([]);
      onChangeCustom([]);
      setAllSelected(false);
      return;
    }
    const allSuppliers: SupplierInfoList[] = options.map(option => ({
      guidSupplier: option.guid
    }));

    setOptionsSelected(allSuppliers);
    onChangeCustom(allSuppliers);
    setAllSelected(true);
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);

    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      formFiltersOnLoadSearchGrid.pageIndex = index;

      setFormFilters(formFiltersOnLoadSearchGrid);
      getGridData(formFiltersOnLoadSearchGrid);
    }
  }

  function handleRefundLimitChange(guidSupplier: string, value: string) {
    const refundLimitValue = !isNil(currencyToNumber(value))
      ? Number(currencyToNumber(value))
      : undefined;

    const clone = lodash.cloneDeep(optionsSelected);
    const index = clone.findIndex(item => item.guidSupplier === guidSupplier);

    if (index > -1) {
      clone[index].refundLimit = refundLimitValue;
    } else {
      clone.push({ guidSupplier, refundLimit: refundLimitValue });
    }

    setOptionsSelected(clone);
    onChangeCustom?.(clone);
  }

  return (
    <S.Content>
      <BoxGrid
        typeGrid="default"
        sizeCell="medium"
        rowHeaders={[
          ...rowHeaders,
          showRefundLimitColumn
            ? getTranslateByKey('information.refundLimit')
            : null,
          showRefundLimitColumn
            ? getTranslateByKey('information.trustedSupplier')
            : null,
          <KogCheckbox
            key="checkbox"
            checked={allSelected}
            value={allSelected}
            onChange={handleToggleSelectAll}
            id="id-checkbox-selectAll"
          />
        ].filter(item => !isNull(item))}
        infiniteScrollConfig={{
          dataLength: options.length,
          fetchData: () => onLoadSearchGrid(),
          hasMore: hasShowMore,
          isLoading: isLoadingInfiniteScroll,
          idScrollTarget: idScrollTargetGrid
        }}>
        {options.map((option, index) => {
          const selected = optionsSelected.find(
            item => item.guidSupplier === option.guid
          );

          const selectedIstrusted = optionsSelected.find(
            item => item.guidSupplier === option.guid && item.isTrusted
          );

          return (
            <TableRow key={option.guid}>
              <TableCell
                width={showRefundLimitColumn ? '40%' : '50%'}
                style={{ wordBreak: 'break-word' }}>
                {option.rfcSupplier}
              </TableCell>

              {showRefundLimitColumn && control && (
                <>
                  <TableCell>
                    <KogInputAmountControlled
                      acceptZero
                      typeInput="outlined"
                      id={`refund-${option.guid}`}
                      name={`refundLimit.${option.guid}`}
                      control={control}
                      controllerProps={{
                        rules: { maxLength: 18 }
                      }}
                      customInputProps={{ disabled: !selected }}
                      onBlurCustom={(value: string) =>
                        handleRefundLimitChange(option.guid, value)
                      }
                    />
                  </TableCell>
                  <TableCell width={'30%'}>
                    <ContainerSwitch>
                      <Switch
                        color="primary"
                        id={`id-checkbox-select-trusted-${index}`}
                        checked={!!selectedIstrusted}
                        disabled={!selected}
                        onChange={(_, checked) =>
                          handleToggleCheckbox(option.guid, checked)
                        }
                      />
                    </ContainerSwitch>
                  </TableCell>
                </>
              )}

              <TableCell width="1%">
                <KogCheckbox
                  className="ignore-click"
                  onChange={() => handleToggleCheckbox(option.guid)}
                  checked={!!selected}
                  id={`id-checkbox-select${index}`}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </BoxGrid>
    </S.Content>
  );
}

export default KOGBoxGridCheckboxInverted;
